import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Sparkles, X, Check, Star, Feather, Loader2, Users } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const annualTotal = (m) => Math.round(m * 12 * 0.7); // -30%
const annualPerMonth = (m) => (annualTotal(m) / 12).toFixed(2).replace(".", ",");

function PlanCard({ name, monthly, annual, tagline, features, recommended, recommendedLabel, ctaLabel, loading, onCheckout, delay, t }) {
  const price = annual ? annualTotal(monthly) : monthly;
  const period = annual ? t("tarifs.perYear") : t("tarifs.perMonth");
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
      data-testid={`upsell-plan-${recommended ? "elan" : "cocon"}`}
      className={`relative rounded-2xl p-6 border overflow-hidden flex flex-col ${
        recommended
          ? "border-primary/50 bg-primary/[0.06] frame-pulse"
          : "border-white/10 bg-white/[0.02]"
      }`}
    >
      {recommended && (
        <div className="absolute top-0 right-0 flex items-center gap-1.5 text-[0.65rem] uppercase tracking-[0.18em] text-primary-foreground bg-primary px-3 py-1.5 rounded-bl-2xl">
          <Star size={11} className="fill-current" /> {recommendedLabel}
        </div>
      )}
      <div className="flex items-center gap-2 mb-1">
        {recommended ? <Sparkles size={17} className="text-primary" /> : <Feather size={17} className="text-primary" />}
        <h3 className="font-display text-xl text-foreground">{name}</h3>
      </div>
      <p className="text-sm text-foreground/50 italic mb-4">{tagline}</p>
      <div className="flex items-baseline gap-1.5">
        <span className="text-3xl font-display text-gradient-orange" data-testid={`upsell-price-${recommended ? "elan" : "cocon"}`}>{price} €</span>
        <span className="text-xs text-foreground/40">/ {period}</span>
      </div>
      <p className="text-[0.7rem] text-foreground/40 mb-5 h-4">
        {annual ? t("tarifs.billedAnnually", { m: annualPerMonth(monthly) }) : ""}
      </p>
      <ul className="space-y-2.5 mb-6 flex-1">
        {(Array.isArray(features) ? features : []).map((f, i) => (
          <li key={i} className="flex items-start gap-2.5 text-sm text-foreground/75 leading-snug">
            <Check size={15} className="text-primary mt-0.5 flex-shrink-0" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <button
        data-testid={`upsell-checkout-${recommended ? "elan" : "cocon"}`}
        onClick={onCheckout}
        disabled={!!loading}
        className={`w-full h-11 rounded-full font-medium text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-50 ${
          recommended
            ? "bg-primary text-primary-foreground hover:bg-primary/90"
            : "border border-primary/40 text-primary hover:bg-primary/10"
        }`}
      >
        {loading ? <Loader2 size={16} className="animate-spin" /> : recommended && <Sparkles size={15} />}
        {ctaLabel}
      </button>
    </motion.div>
  );
}

export default function UpsellModal({ open, reason, onClose }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [annual, setAnnual] = useState(false);
  const [social, setSocial] = useState(null);

  useEffect(() => {
    if (!open) return;
    api.get("/stats/public").then(({ data }) => setSocial(data)).catch(() => {});
  }, [open]);

  const socialLine = social
    ? social.subscribers >= 10
      ? t("atelier.upsellModal.socialSubscribers", { count: social.subscribers })
      : social.explorers > 0
        ? t("atelier.upsellModal.socialExplorers", { count: social.explorers })
        : null
    : null;

  const checkout = async (planId) => {
    setLoadingPlan(planId);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: `${planId}_${annual ? "yearly" : "monthly"}`,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("atelier.silent"));
      setLoadingPlan(null);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="upsell-modal"
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6"
          onClick={onClose}
        >
          <div className="absolute inset-0 bg-background/80 backdrop-blur-md" />
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative glass rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto scroll-thin border border-primary/15 shadow-2xl"
          >
            <div className="cinematic-glow pointer-events-none" aria-hidden />
            <button
              data-testid="upsell-close-btn"
              onClick={onClose}
              className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full flex items-center justify-center text-foreground/40 hover:text-foreground hover:bg-white/10 transition-colors"
              aria-label={t("atelier.upsellModal.later")}
            >
              <X size={18} />
            </button>

            <div className="p-7 sm:p-10">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles size={16} className="text-primary pulse-star" />
                <span className="uppercase tracking-[0.24em] text-xs text-primary/90">
                  {t("atelier.upsellModal.eyebrow")}
                </span>
              </div>

              <h2 className="font-display text-3xl sm:text-4xl text-gradient-orange leading-tight mb-4">
                {t("atelier.upsellModal.title")}
              </h2>

              <p className="font-display italic text-lg text-foreground/80 leading-relaxed mb-2">
                {t("atelier.upsellModal.intro")}
              </p>
              {reason && (
                <p className="text-sm text-foreground/45 mb-2" data-testid="upsell-reason">
                  {reason}
                </p>
              )}
              <p className="text-sm text-foreground/60 leading-relaxed mb-6">
                {t("atelier.upsellModal.outro")}
              </p>

              {socialLine && (
                <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-primary/10 border border-primary/20 mb-6" data-testid="upsell-social">
                  <Users size={14} className="text-primary" />
                  <span className="text-xs text-primary/90">{socialLine}</span>
                </div>
              )}

              <div className="flex justify-center mb-6">
                <div className="relative inline-flex items-center p-1 rounded-full glass border border-white/10" data-testid="upsell-billing-toggle">
                  <button
                    data-testid="upsell-billing-monthly"
                    onClick={() => setAnnual(false)}
                    className="relative z-10 px-5 py-2 rounded-full text-sm font-medium"
                  >
                    {!annual && (
                      <motion.span layoutId="upsell-billing-pill" className="absolute inset-0 -z-10 rounded-full bg-primary"
                        transition={{ type: "spring", stiffness: 380, damping: 32 }} />
                    )}
                    <span className={`transition-colors ${!annual ? "text-primary-foreground" : "text-foreground/60"}`}>{t("tarifs.monthly")}</span>
                  </button>
                  <button
                    data-testid="upsell-billing-annual"
                    onClick={() => setAnnual(true)}
                    className="relative z-10 px-5 py-2 rounded-full text-sm font-medium flex items-center gap-2"
                  >
                    {annual && (
                      <motion.span layoutId="upsell-billing-pill" className="absolute inset-0 -z-10 rounded-full bg-primary"
                        transition={{ type: "spring", stiffness: 380, damping: 32 }} />
                    )}
                    <span className={`transition-colors ${annual ? "text-primary-foreground" : "text-foreground/60"}`}>{t("tarifs.annual")}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full transition-colors ${annual ? "bg-primary-foreground/25 text-primary-foreground" : "bg-primary/20 text-primary"}`}>{t("tarifs.save")}</span>
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <PlanCard
                  t={t}
                  name={t("atelier.upsellModal.elanName")}
                  monthly={7}
                  annual={annual}
                  tagline={t("atelier.upsellModal.elanTagline")}
                  features={t("atelier.upsellModal.elanFeatures", { returnObjects: true })}
                  recommended
                  recommendedLabel={t("atelier.upsellModal.recommended")}
                  ctaLabel={t("tiers.elan.cta")}
                  loading={loadingPlan === "elan"}
                  onCheckout={() => checkout("elan")}
                  delay={0.08}
                />
                <PlanCard
                  t={t}
                  name={t("atelier.upsellModal.coconName")}
                  monthly={15}
                  annual={annual}
                  tagline={t("atelier.upsellModal.coconTagline")}
                  features={t("atelier.upsellModal.coconFeatures", { returnObjects: true })}
                  ctaLabel={t("tiers.cocon.cta")}
                  loading={loadingPlan === "cocon"}
                  onCheckout={() => checkout("cocon")}
                  delay={0.16}
                />
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <button
                  data-testid="upsell-cta-btn"
                  onClick={() => navigate("/tarifs")}
                  className="text-sm text-foreground/60 hover:text-primary transition-colors underline underline-offset-4"
                >
                  {t("atelier.upsellModal.cta")}
                </button>
                <button
                  data-testid="upsell-later-btn"
                  onClick={onClose}
                  className="text-sm text-foreground/40 hover:text-foreground/70 transition-colors"
                >
                  {t("atelier.upsellModal.later")}
                </button>
              </div>
              <p className="text-center text-xs text-foreground/35 mt-5">
                {t("atelier.upsellModal.note")}
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
